import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { Box, Grid2, Stack, Typography, useTheme, Zoom, Tooltip } from "@mui/material";
import { EGInput } from "../../../../../MainComponents/Input/EGInput";
import { EGBasicSelect } from "../../../../../MainComponents/Select/EGBasicSelect";
import EGDatePicker from "../../../../../MainComponents/DatePicker/EGDatePicker";
import { InvestigationItemContainer } from "../../components/InvestigationItemContainer";
import { InvestigationButton } from "../../components/InvestigationButton";
import { InvestigationBlockInfo } from "../../components/InvestigationBlockInfo";
import { DrawerContainer } from "../../components/DrawerContainer";
import Icon from "../../../../../Icon/Icon";
import { returnIsValid, useConstants } from "../../Main/useConstants";
import { validateForm, DEPARTMENT_CATALOGUE, SHOW_AREA_FIELD } from "../AddInvolved";
import { SectionedSelect } from "../../../../../MainComponents/Select/SectionedSelect";
import { InputCatalogue } from "../../../../../MainComponents/Catalogues/InputCatalogue";
import { catalogByPart, setReportTagByColor, assignReportToPbpLeader } from "../../../../../../apis";
import { useParams } from "react-router-dom";

// Vista de los involucrados en un DRAWER
export const InvolvedInfo = ({ 
  open,
  onClose,
  selectedInvolved,
  setSelectedInvolved,
  handleSaveInvolveds,
  involvedsInInvestigation,
  isLoading,
  justView,
}) => {

  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const jaguarState = useSelector((state) => state.jaguar);
  const { email, username } = useSelector((state) => state.auth);
  const { enums, selects: { typeUserLabels }, involveds: { statusOpts, involvedTypeOpts, involvedsKindInfo } } = useConstants();

  const [isDeleting, setIsDeleting] = useState(false);

  const indicatedOpts = useMemo(() => {
    const { COMPLAINANT, /* INVOLVEDS_ACCOMPLICE, */ INVOLVEDS_OFFENDER, INVOLVEDS_VICTIM, INVOLVEDS_WITNESS, SYSTEM } = enums.typeUser;
    const usersWanted = [COMPLAINANT, INVOLVEDS_VICTIM, /* INVOLVEDS_ACCOMPLICE, */ INVOLVEDS_OFFENDER, INVOLVEDS_WITNESS, SYSTEM];

    let newIndicatedOpts = [];

    usersWanted.forEach((value) => {
      if (value === COMPLAINANT) {
        newIndicatedOpts.push({
          key: value,
          options: [{
            value: COMPLAINANT,
            label: t("involved.reporter"),
          }],
        });
        return;
      }

      const fromArray = _.get(jaguarState, enums.typeUserRoutes[value])?.formatted || [];

      // Si es SYSTEM, agregar el usuario actual al inicio de la lista
      if (value === SYSTEM && email) {
        const currentUser = {
          value: email,
          label: username || email,
        };
        newIndicatedOpts.push({
          label: typeUserLabels[value],
          key: value,
          options: [currentUser, ...fromArray],
        });
        return;
      }

      newIndicatedOpts.push({
        label: typeUserLabels[value],
        key: value,
        options: fromArray,
      });
    });

    return newIndicatedOpts;
  }, [jaguarState, email, username]);

  const handleUpdateInvolved = async (newInvolved, oldInvolved) => {
    const newKind = newInvolved.kind;
    const { index: newIndex = null, ...cleanedNewInvolved } = newInvolved;
    const { kind: oldKind, index = null } = oldInvolved;

    const newInvolveds = _.cloneDeep(involvedsInInvestigation);
    if (oldInvolved.added) { // Para los involucrados que vienen del reporte
      const involvedKind = _.get(newInvolveds, `${oldInvolved.rType}`);
      const modifiedIndex = involvedKind.findIndex(item=>item.rName===oldInvolved.rName && item.rType===oldInvolved.rType && item.rOccupation===oldInvolved.rOccupation);
      if (modifiedIndex >= 0) {
        involvedKind[modifiedIndex] = newInvolved;
      } else {
        involvedKind.push(newInvolved);
      }
    } else { // Para los involucrados que vienen de investigacion
      if (newKind !== oldKind) {
        newInvolveds[oldKind].splice(index, 1);
        newInvolveds[newKind].push(cleanedNewInvolved);
      } else {
        const involvedKind = _.get(newInvolveds, `${newKind}`);
        involvedKind[index] = cleanedNewInvolved;
      }
    }
    try {
      await handleSaveInvolveds(newInvolveds);
      setSelectedInvolved(newInvolved);
    } catch (error) {
      console.log(error);
    }
  };

  const handleRemoveInvolved = async (object) => {
    const { index, kind } = object;
    const newInvolveds = _.cloneDeep(involvedsInInvestigation);
    newInvolveds[kind].splice(index, 1);
    try {
      await handleSaveInvolveds(newInvolveds);
      handleClose();
    } catch (error) {
      console.log(error);
    }
  };

  const handleDelete = () => {
    handleRemoveInvolved(selectedInvolved);
    setIsDeleting(false);
  }

  const handleClose = () => {
    setSelectedInvolved(null);  
    onClose();
  }

  return(
    <DrawerContainer
      open={open}
      onClose={handleClose}
      borderColor={involvedsKindInfo?.[selectedInvolved?.kind]?.color}
      maxWidth="550px"
    >
      <Box padding="10px 20px 20px 20px">
        <Typography 
          fontSize={28}
          fontWeight={600}
          color={involvedsKindInfo?.[selectedInvolved?.kind]?.color} marginBottom={2}
        >
          {involvedsKindInfo?.[selectedInvolved?.kind]?.title}
        </Typography>
        <Stack spacing={2}>
          <Data
            selectedInvolved={selectedInvolved}
            handleUpdateInvolved={handleUpdateInvolved}
            involvedsKindInfo={involvedsKindInfo}
            involvedTypeOpts={involvedTypeOpts}
            indicatedOpts={indicatedOpts}
            statusOpts={statusOpts}
            isDeleting={isDeleting}
            setIsDeleting={setIsDeleting}
            isLoading={isLoading}
            open={open}
            justView={justView}
          />
          {
            isDeleting &&
            <Stack 
              border={`2px solid ${theme.palette.pink.main}`}
              borderRadius="10px"
              sx={{ padding: "10px 20px 20px 20px" }}
              spacing={1.2}
            >
              <Typography fontSize={28} color="pink" fontWeight={700}>
                {t("involved.important")}
              </Typography>
              <Typography fontSize={16} color="oxfordBlue">
                {t("involved.delete_warning")}
              </Typography>
              <Typography fontSize={16} color="primary" fontWeight={600}>
                {t("involved.confirm_proceed")}
              </Typography>
              <InvestigationButton
                label={t("involved.cancel")}
                variant="main"
                fullWidth
                outlined
                onClick={() => setIsDeleting(false)}
              />
              <InvestigationButton
                label={t("involved.confirm")}
                variant="main"
                fullWidth
                onClick={handleDelete}
              />
            </Stack>
          }
        </Stack>
      </Box>
    </DrawerContainer>
  )
}

// Componente para editar y ver la informacion del involucrado
const Data = ({
  selectedInvolved,
  handleUpdateInvolved,
  involvedsKindInfo,
  involvedTypeOpts,
  indicatedOpts,
  statusOpts,
  isDeleting,
  setIsDeleting,
  isLoading,
  open,
  justView,
}) => {

  const [t] = useTranslation("investigation");
  const theme = useTheme();
  const { id: reportId } = useParams();

  const { enums, functions, involveds: { reinsidenceOpts } } = useConstants();

  const isManagerOpts = [
    { label: t("involved.yes"), value: "yes" },
    { label: t("involved.no"), value: "no" },
  ];

  const [kind, setKind] = useState("");
  const [name, setName] = useState("");
  const [additional, setAdditional] = useState("");
  const [area, setArea] = useState("");
  const [department, setDepartment] = useState("");
  const [departmentPath, setDepartmentPath] = useState("");
  const [relationship, setRelationship] = useState("");
  const [indicated, setIndicated] = useState(null);
  const [status, setStatus] = useState("");
  const [details, setDetails] = useState("");
  const [gender, setGender] = useState("");
  const [reinsidence, setReinsidence] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [triedToSave, setTriedToSave] = useState(false);
  const [areaOptions, setAreaOptions] = useState([]);
  const [workstationOptions, setWorkstationOptions] = useState([]);
  const [genderOptions, setGenderOptions] = useState([]);

  // Campos para offender
  const [companyRelationship, setCompanyRelationship] = useState("");
  const [meliEntryDate, setMeliEntryDate] = useState(null);
  const [rfcTaxId, setRfcTaxId] = useState("");
  const [companyRelationshipOptions, setCompanyRelationshipOptions] = useState([]);

  // Campos adicionales para accomplice - Comentado temporalmente
  // const [temporarySeparation, setTemporarySeparation] = useState("");
  // const [separationStartDate, setSeparationStartDate] = useState(null);
  // const [separationEndDate, setSeparationEndDate] = useState(null);
  // const [vertical, setVertical] = useState("");
  // const [position, setPosition] = useState("");
  // const [separationOptions, setSeparationOptions] = useState([]);
  const [isManager, setIsManager] = useState("");

  // Campos adicionales del buscador
  const [involvedEmail, setInvolvedEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [country, setCountry] = useState("");

  // Estado para asignación a PBP Leader y PBP
  const [isAssigningPbpLeader, setIsAssigningPbpLeader] = useState(false);
  const [isAssigningPbp, setIsAssigningPbp] = useState(false);

  // Función para asignar denuncia a PBP Leader
  const handleAssignToPbpLeader = async () => {
    // Obtener pbpLeader de pbpLeaderInfo o relationships.pbpLeader
    const pbpLeaderData = selectedInvolved?.pbpLeaderInfo || selectedInvolved?.relationships?.pbpLeader;
    if (!pbpLeaderData || !reportId) return;

    setIsAssigningPbpLeader(true);
    try {
      await assignReportToPbpLeader({
        report_id: reportId,
        email: pbpLeaderData.email,
        first_name: pbpLeaderData.firstName,
        last_name: pbpLeaderData.lastName,
      });
      enqueueSnackbar({
        variant: "success",
        message: t("involved.pbp_leader_assignment_success"),
      });
    } catch (error) {
      console.error("Error assigning to PBP Leader:", error);
      enqueueSnackbar({
        variant: "error",
        message: t("involved.pbp_leader_assignment_error"),
      });
    } finally {
      setIsAssigningPbpLeader(false);
    }
  };

  // Función para asignar denuncia a PBP
  const handleAssignToPbp = async () => {
    // Obtener pbp de pbp o relationships.pbp
    const pbpData = selectedInvolved?.relationships?.pbp || selectedInvolved?.pbp;
    if (!pbpData || !reportId) return;

    setIsAssigningPbp(true);
    try {
      await assignReportToPbpLeader({
        report_id: reportId,
        email: pbpData.email,
        first_name: pbpData.firstName,
        last_name: pbpData.lastName,
      });
      enqueueSnackbar({
        variant: "success",
        message: t("involved.pbp_assignment_success"),
      });
    } catch (error) {
      console.error("Error assigning to PBP:", error);
      enqueueSnackbar({
        variant: "error",
        message: t("involved.pbp_assignment_error"),
      });
    } finally {
      setIsAssigningPbp(false);
    }
  };

  useEffect(() => {
    if (!open) {
      handleClose();
    }
  }, [open]);

  // Cargar opciones del catálogo areaOfExpertise
  useEffect(() => {
    const loadAreaOptions = async () => {
      try {
        const response = await catalogByPart({
          catalogue: "areaOfExpertise",
          is_own: true,
          path: "/"
        });
        if (response?.data) {
          const options = response.data.map((item) => ({
            label: item.label,
            value: item.path_locales,
          }));
          setAreaOptions(options);
        }
      } catch (error) {
        console.error("Error loading area of expertise catalogue:", error);
        setAreaOptions([]);
      }
    };

    loadAreaOptions();
  }, []);

  // Cargar opciones del catálogo workstation
  useEffect(() => {
    const loadWorkstationOptions = async () => {
      try {
        const response = await catalogByPart({
          catalogue: "workstation",
          is_own: true,
          path: "/"
        });
        if (response?.data) {
          const options = response.data.map((item) => ({
            label: item.label,
            value: item.path_locales,
          }));
          setWorkstationOptions(options);
        }
      } catch (error) {
        console.error("Error loading workstation catalogue:", error);
        setWorkstationOptions([]);
      }
    };

    loadWorkstationOptions();
  }, []);

  // Cargar opciones del catálogo genderOfTheInvolvedPerson
  useEffect(() => {
    const loadGenderOptions = async () => {
      try {
        const response = await catalogByPart({
          catalogue: "genderOfTheInvolvedPerson",
          is_own: true,
          path: "/"
        });
        if (response?.data) {
          const options = response.data.map((item) => ({
            label: item.label,
            value: item.path_locales,
          }));
          setGenderOptions(options);
        }
      } catch (error) {
        console.error("Error loading gender catalogue:", error);
        setGenderOptions([]);
      }
    };

    loadGenderOptions();
  }, []);

  // Cargar opciones del catálogo companyRelationshipOffender
  useEffect(() => {
    const loadCompanyRelationshipOptions = async () => {
      try {
        const response = await catalogByPart({
          catalogue: "companyRelationshipOffender",
          is_own: true,
          path: "/"
        });
        if (response?.data) {
          const options = response.data.map((item) => ({
            label: item.label,
            value: item.path_locales,
          }));
          setCompanyRelationshipOptions(options);
        }
      } catch (error) {
        console.error("Error loading company relationship catalogue:", error);
        setCompanyRelationshipOptions([]);
      }
    };

    loadCompanyRelationshipOptions();
  }, []);

  // Cargar opciones del catálogo separation - Comentado temporalmente (para accomplice)
  // useEffect(() => {
  //   const loadSeparationOptions = async () => {
  //     try {
  //       const response = await catalogByPart({
  //         catalogue: "separation",
  //         is_own: true,
  //         path: "/"
  //       });
  //       if (response?.data) {
  //         const options = response.data.map((item) => ({
  //           label: item.label,
  //           value: item.path_locales,
  //         }));
  //         setSeparationOptions(options);
  //       }
  //     } catch (error) {
  //       console.error("Error loading separation catalogue:", error);
  //       setSeparationOptions([]);
  //     }
  //   };

  //   loadSeparationOptions();
  // }, []);

  useEffect(() => {
    if (selectedInvolved) {
      setKind(selectedInvolved.kind ?? "");
      setName(selectedInvolved.name ?? "");
      setAdditional(selectedInvolved.additional ?? "");
      setArea(selectedInvolved.area ?? "");
      setDepartment(selectedInvolved.department ?? "");
      setDepartmentPath(selectedInvolved.departmentPath ?? "");
      setRelationship(selectedInvolved.relationship ?? "");
      setIndicated(selectedInvolved.indicated ?? null);
      setStatus(selectedInvolved.confirm_text ?? (selectedInvolved.confirm ? "confirmed" : "pending"));
      setDetails(selectedInvolved.details ?? "");
      setGender(selectedInvolved.gender ?? "");
      setReinsidence(selectedInvolved.reinsidence ?? "");
      // Campos para offender (y accomplice comentado temporalmente)
      setCompanyRelationship(selectedInvolved.companyRelationship ?? "");
      setMeliEntryDate(selectedInvolved.meliEntryDate ? dayjs(selectedInvolved.meliEntryDate) : null);
      setRfcTaxId(selectedInvolved.rfcTaxId ?? "");
      // Campos adicionales para accomplice - Comentado temporalmente
      // setTemporarySeparation(selectedInvolved.temporarySeparation ?? "");
      // setSeparationStartDate(selectedInvolved.separationStartDate ? dayjs(selectedInvolved.separationStartDate) : null);
      // setSeparationEndDate(selectedInvolved.separationEndDate ? dayjs(selectedInvolved.separationEndDate) : null);
      // setVertical(selectedInvolved.vertical ?? "");
      // setPosition(selectedInvolved.position ?? "");
      setIsManager(selectedInvolved.isManager ?? "");
      // Campos adicionales del buscador
      setInvolvedEmail(selectedInvolved.email ?? "");
      setBusinessName(selectedInvolved.businessName ?? "");
      setCountry(selectedInvolved.country ?? "");
    }
    setErrors({});
    setTriedToSave(false);
  }, [isEditing]);

  // Validate every change
  useEffect(() => {
    if (triedToSave) {
      const [_, errors] = validateForm({ kind, name, indicated, status });
      setErrors(errors);
    }
  }, [kind, name, indicated, status]);

  const handleSave = async () => {
    const isOffender = kind === "offender";
    // const isAccomplice = kind === "accomplice"; // Comentado temporalmente
    const [isValid, errors] = validateForm({ kind, name, indicated, status });
    setErrors(errors);
    setTriedToSave(true);
    if (!isValid) {
      enqueueSnackbar({ variant: "error", message: t("missing_required_inputs") });
      return;
    };
    await handleUpdateInvolved({
      ...selectedInvolved,
      kind,
      rol: kind,
      name,
      additional,
      area,
      department,
      departmentPath,
      relationship,
      indicated,
      gender,
      reinsidence,
      validated: false,
      confirm: status === "confirmed" ? true : false,
      confirm_text: status,
      details,
      isManager,
      // Campos adicionales del buscador
      email: involvedEmail,
      businessName,
      country,
      // Campos para offender (y accomplice comentado temporalmente)
      ...(isOffender /* || isAccomplice */ ? {
        companyRelationship,
        meliEntryDate: meliEntryDate ? dayjs(meliEntryDate).format("YYYY-MM-DD") : "",
        rfcTaxId,
        // temporarySeparation, // Comentado temporalmente (para accomplice)
        // separationStartDate: separationStartDate ? dayjs(separationStartDate).format("YYYY-MM-DD") : "", // Comentado temporalmente (para accomplice)
        // separationEndDate: separationEndDate ? dayjs(separationEndDate).format("YYYY-MM-DD") : "", // Comentado temporalmente (para accomplice)
        // vertical, // Comentado temporalmente (para accomplice)
        // position, // Comentado temporalmente (para accomplice)
      } : {}),
    }, selectedInvolved);

    // Si el involucrado es manager, agregar tag rojo al reporte
    if (isManager === "yes" && selectedInvolved.isManager !== "yes") {
      try {
        await setReportTagByColor({ report_id: reportId, tag: "red" });
        enqueueSnackbar({
          variant: "info",
          message: t("involved.manager_tag_added")
        });
      } catch (error) {
        console.error("Error adding red tag:", error);
      }
    }

    setIsEditing(false);
  }

  const handleCancel = () => {
    setIsEditing(false);
    setKind("");
    setName("");
    setAdditional("");
    setArea("");
    setRelationship("");
    setIndicated(null);
    setStatus("");
    setDetails("");
    setGender("");
    setReinsidence("");
    // Limpiar campos de offender (accomplice comentado temporalmente)
    setCompanyRelationship("");
    setMeliEntryDate(null);
    setRfcTaxId("");
    // setTemporarySeparation(""); // Comentado temporalmente (para accomplice)
    // setSeparationStartDate(null); // Comentado temporalmente (para accomplice)
    // setSeparationEndDate(null); // Comentado temporalmente (para accomplice)
    // setVertical(""); // Comentado temporalmente (para accomplice)
    // setPosition(""); // Comentado temporalmente (para accomplice)
    setIsManager("");
    // Campos adicionales del buscador
    setInvolvedEmail("");
    setBusinessName("");
    setCountry("");
  }

  const handleClose = () => {
    handleCancel();
    setErrors({});
    setTriedToSave(false);
  }

  return (
    <InvestigationItemContainer color={involvedsKindInfo?.[selectedInvolved?.kind]?.color} sx={{ padding: "10px 20px 20px 20px" }}>

      <Box display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
        <Typography fontSize={16} fontWeight={600} color={involvedsKindInfo?.[selectedInvolved?.kind]?.color} >
          {selectedInvolved?.name}
        </Typography>
        <Tooltip
            title={
              selectedInvolved?.confirm_text === "confirmed" ? 
              t("involved.confirmed") :
              selectedInvolved?.confirm_text === "cant_confirm" ? 
              t("involved.not_confirmed") :
              selectedInvolved?.confirm_text === "out" ? 
              t("involved.not_participated") :
              selectedInvolved?.confirm_text === true ? 
              t("involved.confirmed") :
              t("involved.pending_confirmation")
            }
            placement="bottom"
            arrow
            TransitionComponent={Zoom}
            TransitionProps={{ timeout: 150 }}
          >
            <Box ml={.5}>
              <Icon 
                name={
                  selectedInvolved?.confirm_text === "confirmed" ? 
                  "bold_tick_circle" :
                  selectedInvolved?.confirm_text === "cant_confirm" ? 
                  "bold_danger" :
                  selectedInvolved?.confirm_text === "out" ? 
                  "bold_close_circle" :
                  selectedInvolved?.confirm_text === true ? 
                  "bold_tick_circle" :
                  "bold_info_circle"
                }
                color={
                  selectedInvolved?.confirm_text === "confirmed" ? 
                  theme.palette.green.main :
                  selectedInvolved?.confirm_text === "cant_confirm" ? 
                  theme.palette.orange.main :
                  selectedInvolved?.confirm_text === "out" ? 
                  theme.palette.red.main :
                  selectedInvolved?.confirm_text === true ? 
                  theme.palette.green.main :
                  theme.palette.yellow.main
                }
                size={16} 
              />
            </Box>
          </Tooltip>
      </Box>
      
      <hr style={{ color: theme.palette.secondary.main, height: "1px", border: "none" }} />
      
      {
        !isEditing &&
        <Stack spacing={2}>

          {/* ═══════════════════════════════════════════════════════════════
              SECCIÓN: Información Básica
          ═══════════════════════════════════════════════════════════════ */}
          {(selectedInvolved?.email || selectedInvolved?.username || selectedInvolved?.userId || selectedInvolved?.additional || selectedInvolved?.employeeType) && (
            <Box
              sx={{
                backgroundColor: `${theme.palette.primary.main}08`,
                borderRadius: "10px",
                padding: "12px 16px",
                border: `1px solid ${theme.palette.primary.main}20`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <Icon name="bold_user" color={theme.palette.primary.main} size={18} />
                <Typography fontSize={13} fontWeight={700} color={theme.palette.primary.main}>
                  {t("involved.section_basic_info")}
                </Typography>
              </Stack>
              <Grid2 container spacing={1.5}>
                {selectedInvolved?.userId && (
                  <Grid2 size={{ xs: 6 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.user_id")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.userId}</Typography>
                  </Grid2>
                )}
                {selectedInvolved?.username && (
                  <Grid2 size={{ xs: 6 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.username")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.username}</Typography>
                  </Grid2>
                )}
                {selectedInvolved?.additional && (
                  <Grid2 size={{ xs: 6 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.employee_number")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.additional}</Typography>
                  </Grid2>
                )}
                {selectedInvolved?.employeeType && (
                  <Grid2 size={{ xs: 6 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.employee_type")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.employeeType === "internal" ? t("involved.internal") : t("involved.external")}</Typography>
                  </Grid2>
                )}
                {selectedInvolved?.email && (
                  <Grid2 size={{ xs: 12 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.email")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.email}</Typography>
                  </Grid2>
                )}
              </Grid2>
            </Box>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              SECCIÓN: Organización
          ═══════════════════════════════════════════════════════════════ */}
          {(selectedInvolved?.division || selectedInvolved?.department || selectedInvolved?.businessUnit || selectedInvolved?.company || selectedInvolved?.area) && (
            <Box
              sx={{
                backgroundColor: `${theme.palette.teal.main}08`,
                borderRadius: "10px",
                padding: "12px 16px",
                border: `1px solid ${theme.palette.teal.main}20`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <Icon name="bold_building" color={theme.palette.teal.main} size={18} />
                <Typography fontSize={13} fontWeight={700} color={theme.palette.teal.main}>
                  {t("involved.section_organization")}
                </Typography>
              </Stack>
              <Grid2 container spacing={1.5}>
                {selectedInvolved?.division && (
                  <Grid2 size={{ xs: 6 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.division")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.division}</Typography>
                  </Grid2>
                )}
                {selectedInvolved?.department && (
                  <Grid2 size={{ xs: 6 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.department")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.department}</Typography>
                  </Grid2>
                )}
                {selectedInvolved?.businessUnit && (
                  <Grid2 size={{ xs: 6 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.business_unit")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.businessUnit}</Typography>
                  </Grid2>
                )}
                {selectedInvolved?.company && (
                  <Grid2 size={{ xs: 6 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.company")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.company}</Typography>
                  </Grid2>
                )}
                {selectedInvolved?.area && (
                  <Grid2 size={{ xs: 12 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.area")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.area}</Typography>
                  </Grid2>
                )}
              </Grid2>
            </Box>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              SECCIÓN: Ubicación
          ═══════════════════════════════════════════════════════════════ */}
          {(selectedInvolved?.country || selectedInvolved?.province || selectedInvolved?.locationName) && (
            <Box
              sx={{
                backgroundColor: `${theme.palette.blue.main}08`,
                borderRadius: "10px",
                padding: "12px 16px",
                border: `1px solid ${theme.palette.blue.main}20`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <Icon name="bold_location" color={theme.palette.blue.main} size={18} />
                <Typography fontSize={13} fontWeight={700} color={theme.palette.blue.main}>
                  {t("involved.section_location")}
                </Typography>
              </Stack>
              <Grid2 container spacing={1.5}>
                {selectedInvolved?.country && (
                  <Grid2 size={{ xs: 6 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.country")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.country}</Typography>
                  </Grid2>
                )}
                {selectedInvolved?.province && (
                  <Grid2 size={{ xs: 6 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.province")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.province}</Typography>
                  </Grid2>
                )}
                {selectedInvolved?.locationName && (
                  <Grid2 size={{ xs: 12 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.location")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.locationName}</Typography>
                  </Grid2>
                )}
              </Grid2>
            </Box>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              SECCIÓN: Puesto
          ═══════════════════════════════════════════════════════════════ */}
          {(selectedInvolved?.position || selectedInvolved?.relationship || selectedInvolved?.jobCode || selectedInvolved?.costCenter) && (
            <Box
              sx={{
                backgroundColor: `${theme.palette.purple.main}08`,
                borderRadius: "10px",
                padding: "12px 16px",
                border: `1px solid ${theme.palette.purple.main}20`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <Icon name="bold_briefcase" color={theme.palette.purple.main} size={18} />
                <Typography fontSize={13} fontWeight={700} color={theme.palette.purple.main}>
                  {t("involved.section_position")}
                </Typography>
              </Stack>
              <Grid2 container spacing={1.5}>
                {(selectedInvolved?.position || selectedInvolved?.relationship) && (
                  <Grid2 size={{ xs: 12 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.cargo")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.position || selectedInvolved.relationship}</Typography>
                  </Grid2>
                )}
                {selectedInvolved?.jobCode && (
                  <Grid2 size={{ xs: 6 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.job_code")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.jobCode}</Typography>
                  </Grid2>
                )}
                {selectedInvolved?.costCenter && (
                  <Grid2 size={{ xs: 6 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.cost_center")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.costCenter}</Typography>
                  </Grid2>
                )}
              </Grid2>
            </Box>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              SECCIÓN: Fechas
          ═══════════════════════════════════════════════════════════════ */}
          {(selectedInvolved?.hireDate || selectedInvolved?.positionEntryDate || selectedInvolved?.meliEntryDate) && (
            <Box
              sx={{
                backgroundColor: `${theme.palette.orange.main}08`,
                borderRadius: "10px",
                padding: "12px 16px",
                border: `1px solid ${theme.palette.orange.main}20`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <Icon name="bold_calendar" color={theme.palette.orange.main} size={18} />
                <Typography fontSize={13} fontWeight={700} color={theme.palette.orange.main}>
                  {t("involved.section_dates")}
                </Typography>
              </Stack>
              <Grid2 container spacing={1.5}>
                {selectedInvolved?.hireDate && (
                  <Grid2 size={{ xs: 6 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.hire_date")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.hireDate}</Typography>
                  </Grid2>
                )}
                {selectedInvolved?.positionEntryDate && (
                  <Grid2 size={{ xs: 6 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.position_entry_date")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.positionEntryDate}</Typography>
                  </Grid2>
                )}
                {selectedInvolved?.meliEntryDate && (
                  <Grid2 size={{ xs: 6 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.meli_entry_date")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.meliEntryDate}</Typography>
                  </Grid2>
                )}
              </Grid2>
            </Box>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              SECCIÓN: Identificaciones
          ═══════════════════════════════════════════════════════════════ */}
          {(selectedInvolved?.primaryNationalId?.nationalId || selectedInvolved?.rfcTaxId || selectedInvolved?.nationalIds?.length > 0) && (
            <Box
              sx={{
                backgroundColor: `${theme.palette.pink.main}08`,
                borderRadius: "10px",
                padding: "12px 16px",
                border: `1px solid ${theme.palette.pink.main}20`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <Icon name="bold_card" color={theme.palette.pink.main} size={18} />
                <Typography fontSize={13} fontWeight={700} color={theme.palette.pink.main}>
                  {t("involved.section_identifications")}
                </Typography>
              </Stack>
              <Grid2 container spacing={1.5}>
                {selectedInvolved?.primaryNationalId?.nationalId && (
                  <Grid2 size={{ xs: 12 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.national_id")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">
                      {`${selectedInvolved.primaryNationalId.country || ""} - ${selectedInvolved.primaryNationalId.nationalId}`}
                    </Typography>
                  </Grid2>
                )}
                {selectedInvolved?.rfcTaxId && (
                  <Grid2 size={{ xs: 12 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.rfc_tax_id")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.rfcTaxId}</Typography>
                  </Grid2>
                )}
                {selectedInvolved?.nationalIds?.length > 1 && (
                  <Grid2 size={{ xs: 12 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.other_national_ids")}</Typography>
                    {selectedInvolved.nationalIds
                      .filter(id => id.nationalId !== selectedInvolved?.primaryNationalId?.nationalId)
                      .map((id, index) => (
                        <Typography key={index} fontSize={12} color="oxfordBlue.main">
                          {`${id.country || ""} (${id.cardType || ""}) - ${id.nationalId || ""}`}
                        </Typography>
                      ))
                    }
                  </Grid2>
                )}
              </Grid2>
            </Box>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              SECCIÓN: Relaciones Laborales
          ═══════════════════════════════════════════════════════════════ */}
          {(selectedInvolved?.manager?.userId || selectedInvolved?.pbp?.relUserId || selectedInvolved?.pbpLeaderInfo?.userId || selectedInvolved?.taOwner?.relUserId || selectedInvolved?.relationships?.pbp || selectedInvolved?.relationships?.pbpLeader || selectedInvolved?.relationships?.taOwner) && (
            <Box
              sx={{
                backgroundColor: `${theme.palette.green.main}08`,
                borderRadius: "10px",
                padding: "12px 16px",
                border: `1px solid ${theme.palette.green.main}20`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <Icon name="bold_people" color={theme.palette.green.main} size={18} />
                <Typography fontSize={13} fontWeight={700} color={theme.palette.green.main}>
                  {t("involved.section_relationships")}
                </Typography>
              </Stack>
              <Grid2 container spacing={1.5}>
                {selectedInvolved?.manager?.userId && (
                  <Grid2 size={{ xs: 12 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.manager")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">
                      {`${selectedInvolved.manager.firstName || ""} ${selectedInvolved.manager.lastName || ""}`.trim() || selectedInvolved.manager.email || selectedInvolved.manager.userId}
                    </Typography>
                    {selectedInvolved.manager.email && (
                      <Typography fontSize={11} color="secondary">{selectedInvolved.manager.email}</Typography>
                    )}
                  </Grid2>
                )}
                {/* PBP Leader - desde pbpLeaderInfo o relationships.pbpLeader */}
                {(selectedInvolved?.pbpLeaderInfo?.userId || selectedInvolved?.relationships?.pbpLeader) && (
                  <Grid2 size={{ xs: 12 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.pbp_leader")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">
                      {selectedInvolved?.pbpLeaderInfo?.userId
                        ? `${selectedInvolved.pbpLeaderInfo.firstName || ""} ${selectedInvolved.pbpLeaderInfo.lastName || ""}`.trim() || selectedInvolved.pbpLeaderInfo.userId
                        : `${selectedInvolved.relationships.pbpLeader.firstName || ""} ${selectedInvolved.relationships.pbpLeader.lastName || ""}`.trim() || selectedInvolved.relationships.pbpLeader.relUserId
                      }
                    </Typography>
                    <Typography fontSize={11} color="secondary">
                      {selectedInvolved?.pbpLeaderInfo?.email || selectedInvolved?.relationships?.pbpLeader?.email || ""}
                    </Typography>
                  </Grid2>
                )}
                {/* PBP - desde pbp o relationships.pbp */}
                {(selectedInvolved?.pbp?.relUserId || selectedInvolved?.relationships?.pbp) && (
                  <Grid2 size={{ xs: 12 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.pbp")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">
                      {selectedInvolved?.relationships?.pbp
                        ? `${selectedInvolved.relationships.pbp.firstName || ""} ${selectedInvolved.relationships.pbp.lastName || ""}`.trim() || selectedInvolved.relationships.pbp.relUserId
                        : selectedInvolved.pbp.relUserId
                      }
                    </Typography>
                    {(selectedInvolved?.relationships?.pbp?.email || selectedInvolved?.pbp?.email) && (
                      <Typography fontSize={11} color="secondary">
                        {selectedInvolved?.relationships?.pbp?.email || selectedInvolved?.pbp?.email}
                      </Typography>
                    )}
                  </Grid2>
                )}
                {/* TA Owner - desde taOwner o relationships.taOwner */}
                {(selectedInvolved?.taOwner?.relUserId || selectedInvolved?.relationships?.taOwner) && (
                  <Grid2 size={{ xs: 12 }}>
                    <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.ta_owner")}</Typography>
                    <Typography fontSize={12} color="oxfordBlue.main">
                      {selectedInvolved?.relationships?.taOwner
                        ? `${selectedInvolved.relationships.taOwner.firstName || ""} ${selectedInvolved.relationships.taOwner.lastName || ""}`.trim() || selectedInvolved.relationships.taOwner.relUserId
                        : selectedInvolved.taOwner.relUserId
                      }
                    </Typography>
                    {(selectedInvolved?.relationships?.taOwner?.email || selectedInvolved?.taOwner?.email) && (
                      <Typography fontSize={11} color="secondary">
                        {selectedInvolved?.relationships?.taOwner?.email || selectedInvolved?.taOwner?.email}
                      </Typography>
                    )}
                  </Grid2>
                )}
              </Grid2>
            </Box>
          )}

          {/* ═══════════════════════════════════════════════════════════════
              SECCIÓN: Información de la Investigación
          ═══════════════════════════════════════════════════════════════ */}
          <Box
            sx={{
              backgroundColor: `${theme.palette.secondary.main}08`,
              borderRadius: "10px",
              padding: "12px 16px",
              border: `1px solid ${theme.palette.secondary.main}20`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              <Icon name="bold_document_text" color={theme.palette.secondary.main} size={18} />
              <Typography fontSize={13} fontWeight={700} color={theme.palette.secondary.main}>
                {t("involved.section_investigation")}
              </Typography>
            </Stack>
            <Grid2 container spacing={1.5}>
              <Grid2 size={{ xs: 12 }}>
                <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.indicated_by")}</Typography>
                <Typography fontSize={12} color="oxfordBlue.main">
                  {selectedInvolved?.indicated?.from === selectedInvolved?.added
                    ? functions.getUserLabel({ from: enums.typeUser.COMPLAINANT })
                    : functions.getUserLabel(selectedInvolved?.indicated) ?? t("involved.other")}
                </Typography>
              </Grid2>
              {selectedInvolved?.gender && (
                <Grid2 size={{ xs: 6 }}>
                  <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.gender")}</Typography>
                  <Typography fontSize={12} color="oxfordBlue.main">
                    {genderOptions.find(item => item.value === selectedInvolved.gender)?.label ?? ""}
                  </Typography>
                </Grid2>
              )}
              {selectedInvolved?.reinsidence && (
                <Grid2 size={{ xs: 6 }}>
                  <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.reinsidence")}</Typography>
                  <Typography fontSize={12} color="oxfordBlue.main">
                    {reinsidenceOpts.find(item => item.value === selectedInvolved.reinsidence)?.label ?? ""}
                  </Typography>
                </Grid2>
              )}
              {selectedInvolved?.isManager && (
                <Grid2 size={{ xs: 6 }}>
                  <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.is_manager")}</Typography>
                  <Typography fontSize={12} color="oxfordBlue.main">
                    {isManagerOpts.find(item => item.value === selectedInvolved.isManager)?.label ?? ""}
                  </Typography>
                </Grid2>
              )}
              {selectedInvolved?.companyRelationship && (
                <Grid2 size={{ xs: 6 }}>
                  <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.company_relationship")}</Typography>
                  <Typography fontSize={12} color="oxfordBlue.main">
                    {companyRelationshipOptions.find(item => item.value === selectedInvolved.companyRelationship)?.label ?? ""}
                  </Typography>
                </Grid2>
              )}
              {selectedInvolved?.businessName && (
                <Grid2 size={{ xs: 12 }}>
                  <Typography fontSize={11} fontWeight={600} color="secondary">{t("involved.business_name")}</Typography>
                  <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.businessName}</Typography>
                </Grid2>
              )}
            </Grid2>
          </Box>

          {/* ═══════════════════════════════════════════════════════════════
              SECCIÓN: Asignación de PBP/PBP Leader
          ═══════════════════════════════════════════════════════════════ */}
          {(selectedInvolved?.pbp?.relUserId || selectedInvolved?.relationships?.pbp || selectedInvolved?.pbpLeaderInfo?.userId || selectedInvolved?.relationships?.pbpLeader) && (
            <Box
              sx={{
                backgroundColor: `${theme.palette.warning.main}15`,
                borderRadius: "10px",
                padding: "12px 16px",
                border: `2px solid ${theme.palette.warning.main}60`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <Icon name="bold_user_octagon" color={theme.palette.warning.main} size={20} />
                <Typography fontSize={14} fontWeight={700} color={theme.palette.warning.main}>
                  {t("involved.pbp_assignment_section")}
                </Typography>
              </Stack>

              {/* PBP - desde pbp o relationships.pbp */}
              {(selectedInvolved?.pbp?.relUserId || selectedInvolved?.relationships?.pbp) && (
                <Box
                  sx={{
                    backgroundColor: `${theme.palette.teal.main}10`,
                    borderRadius: "8px",
                    padding: "10px 12px",
                    marginBottom: 1.5,
                    border: `1px solid ${theme.palette.teal.main}30`,
                  }}
                >
                  <Typography fontSize={11} fontWeight={600} color="secondary" mb={0.5}>{t("involved.pbp")}</Typography>
                  <Typography fontSize={13} fontWeight={500} color="oxfordBlue.main">
                    {selectedInvolved?.relationships?.pbp
                      ? `${selectedInvolved.relationships.pbp.firstName || ""} ${selectedInvolved.relationships.pbp.lastName || ""}`.trim() || selectedInvolved.relationships.pbp.relUserId
                      : selectedInvolved.pbp.relUserId
                    }
                  </Typography>
                  {(selectedInvolved?.relationships?.pbp?.email || selectedInvolved?.pbp?.email) && (
                    <Typography fontSize={11} color="secondary">
                      {selectedInvolved?.relationships?.pbp?.email || selectedInvolved?.pbp?.email}
                    </Typography>
                  )}
                  <Box mt={1.5}>
                    <InvestigationButton
                      label={t("involved.assign_to_pbp")}
                      icon="bold_user_add"
                      variant="teal"
                      fullWidth
                      onClick={handleAssignToPbp}
                      loading={isAssigningPbp}
                      disabled={isAssigningPbp}
                      size="medium"
                    />
                  </Box>
                </Box>
              )}

              {/* PBP Leader - desde pbpLeaderInfo o relationships.pbpLeader */}
              {(selectedInvolved?.pbpLeaderInfo?.userId || selectedInvolved?.relationships?.pbpLeader) && (
                <Box
                  sx={{
                    backgroundColor: `${theme.palette.purple.main}10`,
                    borderRadius: "8px",
                    padding: "10px 12px",
                    border: `1px solid ${theme.palette.purple.main}30`,
                  }}
                >
                  <Typography fontSize={11} fontWeight={600} color="secondary" mb={0.5}>{t("involved.pbp_leader")}</Typography>
                  <Typography fontSize={13} fontWeight={500} color="oxfordBlue.main">
                    {selectedInvolved?.pbpLeaderInfo?.userId
                      ? `${selectedInvolved.pbpLeaderInfo.firstName || ""} ${selectedInvolved.pbpLeaderInfo.lastName || ""}`.trim() || selectedInvolved.pbpLeaderInfo.userId
                      : `${selectedInvolved.relationships.pbpLeader.firstName || ""} ${selectedInvolved.relationships.pbpLeader.lastName || ""}`.trim() || selectedInvolved.relationships.pbpLeader.relUserId
                    }
                  </Typography>
                  <Typography fontSize={11} color="secondary">
                    {selectedInvolved?.pbpLeaderInfo?.email || selectedInvolved?.relationships?.pbpLeader?.email || ""}
                  </Typography>
                  <Box mt={1.5}>
                    <InvestigationButton
                      label={t("involved.assign_to_pbp_leader")}
                      icon="bold_user_add"
                      variant="purple"
                      fullWidth
                      onClick={handleAssignToPbpLeader}
                      loading={isAssigningPbpLeader}
                      disabled={isAssigningPbpLeader}
                      size="medium"
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Detalles adicionales */}
          {selectedInvolved?.details && (
            <Box
              sx={{
                backgroundColor: `${theme.palette.blueGray50.main}`,
                borderRadius: "10px",
                padding: "12px 16px",
              }}
            >
              <Typography fontSize={11} fontWeight={600} color="secondary" mb={0.5}>{t("involved.details")}</Typography>
              <Typography fontSize={12} color="oxfordBlue.main">{selectedInvolved.details}</Typography>
            </Box>
          )}
          {/* Read-only indicator for imported users */}
          {
            selectedInvolved?.importedFromSearch &&
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                backgroundColor: `${theme.palette.green.main}10`,
                borderRadius: "8px",
                padding: "8px 12px",
                marginTop: 1,
              }}
            >
              <Icon name="bold_shield_tick" color={theme.palette.green.main} size={18} />
              <Typography fontSize={12} color={theme.palette.green.main} fontWeight={500}>
                {t("involved.validated_from_search")}
              </Typography>
            </Box>
          }
          {
            !justView && !selectedInvolved?.importedFromSearch &&
            <Box display="flex" justifyContent="flex-end">
              {
                !selectedInvolved?.added &&
                <InvestigationButton
                  icon="trash_can"
                  sx={{ marginRight: 1 }}
                  iconColor={isDeleting ? theme.palette.blueGray200.main : theme.palette.red.main}
                  onClick={() => setIsDeleting(true)}
                  disabled={isDeleting}
                  loading={isLoading}
                  size="thin"
                />
              }
              <InvestigationButton
                label={t("involved.edit")}
                icon="outline_edit"
                iconColor={isDeleting ? theme.palette.blueGray200.main : undefined}
                onClick={() => setIsEditing(true)}
                disabled={isDeleting}
                loading={isLoading}
                size="thin"
              />
            </Box>
          }
        </Stack>
      }

      {
        isEditing &&
        <Stack spacing={1.8}>
          <EGBasicSelect
            label={t("involved.type")}
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            options={involvedTypeOpts}
            isValid={returnIsValid(errors.kind, triedToSave)}
            required={true}
            showSuccessValidation={triedToSave}
            showHelpText={false}
          />
          <EGInput
            label={t("involved.full_name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            isValid={returnIsValid(errors.name, triedToSave)}
            required={true}
            showSuccessValidation={triedToSave}
            showHelpText={false}
          />

          <EGInput
            label={t("involved.employee_number")}
            value={additional}
            onChange={(e) => setAdditional(e.target.value)}
            type="number"
          />

          {SHOW_AREA_FIELD && (
            <EGBasicSelect
              label={t("involved.area")}
              value={area}
              onChange={(e) => setArea(e.target.value)}
              options={areaOptions}
            />
          )}

          <InputCatalogue
            label={t("involved.department")}
            catalogue={DEPARTMENT_CATALOGUE}
            isOwn={true}
            defaultValue={selectedInvolved?.departmentPath ?? ""}
            onChange={(value, option) => {
              setDepartmentPath(value);
              setDepartment(option?.label ?? "");
            }}
          />

          <EGBasicSelect
            label={t("involved.position")}
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
            options={workstationOptions}
          />

          <SectionedSelect
            label={t("involved.indicated_by")}
            value={indicated?.id ?? ""}
            onChange={({ value, section }) => setIndicated({ id: value, from: section })}
            sections={indicatedOpts}
            required
            isValid={returnIsValid(errors?.indicated, triedToSave)}
            showSuccessValidation={true}
            showHelpText={false}
          />

          <EGBasicSelect
            label={t("involved.gender")}
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            options={genderOptions}
          />

          <EGBasicSelect
            label={t("involved.reinsidence")}
            value={reinsidence}
            onChange={(e) => setReinsidence(e.target.value)}
            options={reinsidenceOpts}
          />

          <EGBasicSelect
            label={t("involved.status")}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={statusOpts}
            isValid={returnIsValid(errors.status, triedToSave)}
            required={true}
            showSuccessValidation={triedToSave}
            showHelpText={false}
          />

          <EGBasicSelect
            label={t("involved.is_manager")}
            value={isManager}
            onChange={(e) => setIsManager(e.target.value)}
            options={isManagerOpts}
          />

          {/* Campos para offender (accomplice comentado temporalmente) */}
          {
            (kind === "offender" /* || kind === "accomplice" */) &&
            <>
              <EGBasicSelect
                label={t("involved.company_relationship")}
                value={companyRelationship}
                onChange={(e) => setCompanyRelationship(e.target.value)}
                options={companyRelationshipOptions}
              />

              <EGDatePicker
                label={t("involved.meli_entry_date")}
                value={meliEntryDate}
                onChange={(newValue) => setMeliEntryDate(newValue)}
              />

              <EGInput
                label={t("involved.rfc_tax_id")}
                value={rfcTaxId}
                onChange={(e) => setRfcTaxId(e.target.value)}
              />
            </>
          }

          {/* Campos adicionales del buscador */}
          <EGInput
            label={t("involved.email")}
            value={involvedEmail}
            onChange={(e) => setInvolvedEmail(e.target.value)}
            type="email"
          />

          <EGInput
            label={t("involved.business_name")}
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
          />

          <EGInput
            label={t("involved.country")}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          />

          {/* Campos adicionales para accomplice - Comentado temporalmente */}
          {/* {
            (kind === "offender" || kind === "accomplice") &&
            <>
              <EGBasicSelect
                label={t("involved.temporary_separation")}
                value={temporarySeparation}
                onChange={(e) => setTemporarySeparation(e.target.value)}
                options={separationOptions}
              />

              <EGDatePicker
                label={t("involved.separation_start_date")}
                value={separationStartDate}
                onChange={(newValue) => setSeparationStartDate(newValue)}
              />

              <EGDatePicker
                label={t("involved.separation_end_date")}
                value={separationEndDate}
                onChange={(newValue) => setSeparationEndDate(newValue)}
              />

              <EGInput
                label={t("involved.vertical")}
                value={vertical}
                onChange={(e) => setVertical(e.target.value)}
              />

              <EGInput
                label={t("involved.cargo")}
                value={position}
                onChange={(e) => setPosition(e.target.value)}
              />

              <EGInput
                label={t("involved.email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
              />
            </>
          } */}

          <EGInput
            label={t("involved.details")}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            type="textarea"
            multiline
            rows={5}
          />
          
          <Box display="flex" flexDirection="column">
            <InvestigationButton 
              label={t("involved.cancel")}
              icon="close_icon"
              size="medium"
              fullWidth
              onClick={handleCancel}
              sx={{ marginBottom: 1 }}
            />
            <InvestigationButton 
              label={t("involved.save")}
              variant="success"
              size="medium"
              icon="mark_check_bold"
              fullWidth
              onClick={handleSave}
            />
          </Box>
        </Stack>
      }
    </InvestigationItemContainer>
  )
}